import _ from "lodash";
import ACLStore from "lib/aclStore";
import { getCloudUrl } from "Constants";
import {
  LTI_SERVICE_DETAILS,
  LTI_PLATFORM_CREDS_FIELDS,
  KOGNITY_CREDS_FIELDS,
  LTI_NAME_MAP,
} from "AppComponents/LTIComponent/modules/Utils";
import { KOGNITY } from "Constants/stringConstants";

const LTI_ICON_BASE_URL = `${getCloudUrl()}/assets/integration_lti_icons`;

export const enhanceLTIName = {
  TURNITIN: "turnitin feedback studio",
  SPONTE: "mais serviços",
  MS365: "microsoft education",
  QUIZIZZ: "wayground",
};

export const initialEmptyState = {
  clientId: "",
  clientSecret: "",
  // credType: "LTI_CONFIG", // need to remove
  entityType: "CURRICULUM_PROGRAM",
  deploymentId: "",
  name: "",
  serviceName: "",
  url: {
    launchUrl: "",
    stopLinkCreation: true,
    targetLinkUrl: "",
  },
  metadata: {
    gradePassback: true,
    readingResults: false,
    readingGradebook: false,
    rosterAccess: true,
    iconUrl: "",
    placement: [],
    privacyLevel: "PUBLIC",
    custom_params: {},
    messageType: "CONTENT_ITEM_SELECTION",
    viewResponseAfterSubmission: false,
  },
  version: null,
  entityId: [],
  isToolEnabled: false,
  isDeleted: false,

  // need to send isCreatedOnFrontend boolean and false on edit
};

export const initialEmptyStateV2 = {
  clientId: "",
  clientSecret: "",
  credType: "LTI_CONFIG",
  entityType: "CURRICULUM_PROGRAM",
  messageType: "CONTENT_ITEM_SELECTION_REQUEST",
  serviceName: "",
  url: {
    launchUrl: "",
    stopLinkCreation: true,
  },
  version: null,
  entityId: [],
  isToolEnabled: false,
  isDeleted: false,
  isCreatedOnFrontEnd: true,
  metadata: {
    viewResponseAfterSubmission: false,
  },
};

export const getReverseMapping = state => {
  return _.flatMap(state, group => {
    const customParamsStr = _.get(group, "metadata.custom_params", "");
    const customParamsObj = _.isPlainObject(customParamsStr)
      ? customParamsStr
      : _(customParamsStr)
          .split("\n")
          .filter(Boolean)
          .map(line => line.split("="))
          .filter(([key, value]) => key && value !== undefined)
          .map(([key, ...valParts]) => [key.trim(), valParts.join("=").trim()])
          .fromPairs()
          .value();

    // Parse and normalize launchUrl
    const launchUrl = parseLaunchUrl(group);

    const isLTIVersion1_0 = group.version === "1.0";

    return _.map(group.entityId, entityId => ({
      id: group.id,
      parentId: group.parentId,
      clientId: group.clientId,
      clientSecret: group.clientSecret,
      // credType: group.credType,
      entityType: group.entityType,
      // messageType: group.messageType,
      serviceName: group.serviceName,
      url: { ...group.url, launchUrl },
      version: group.version,
      entityId: entityId,
      isDeleted: group.isDeleted,
      isToolEnabled: group.isToolEnabled,
      metadata: {
        ...group.metadata,
        custom_params: customParamsObj,
        ...(isLTIVersion1_0 && {
          gradePassback: false,
          rosterAccess: false,
        }),
      },
      name: _.trim(group.name),
      // isCreatedOnFrontEnd: group.isCreatedOnFrontEnd || false,
      // toddleCreds: group.toddleCreds || {},
    }));
  });
};

export const getReverseMappingV2 = state => {
  return _.flatMap(state, group => {
    const isLTIVersion1_0 = group.version === "1.0";

    return _.map(group.entityId, entityId => ({
      id: group.id,
      clientId: group.clientId,
      clientSecret: group.clientSecret,
      credType: group.credType,
      entityType: group.entityType,
      messageType: group.messageType,
      serviceName: group.serviceName,
      url: group.url,
      version: group.version,
      entityId: entityId,
      isDeleted: group.isDeleted,
      isToolEnabled: group.isToolEnabled,
      parentId: group.parentId,
      metadata: {
        ...group.metadata,
        ...(isLTIVersion1_0 && {
          gradePassback: false,
          rosterAccess: false,
        }),
      },
    }));
  });
};

export const getInitialState = ltiIntegrationsCreds => {
  if (_.isEmpty(ltiIntegrationsCreds)) {
    return [initialEmptyState];
  }

  const state = _.chain(ltiIntegrationsCreds)
    .groupBy(
      ltiCredsObject =>
        `${ltiCredsObject.clientId}-${ltiCredsObject.clientSecret}-${ltiCredsObject.url.launchUrl}-${ltiCredsObject.serviceName}-${ltiCredsObject.name}-${ltiCredsObject.parentId}`
    )
    .map(group => {
      const {
        clientId,
        clientSecret,
        // credType,
        entityType,
        // messageType,
        serviceName,
        url,
        version,
        isDeleted,
        id,
        isToolEnabled,
        metadata,
        entityId,
        ...rest
      } = _.head(group);

      // Create a mapping from entityId to id
      const entityIdToIdMap = {};
      group.forEach(item => {
        entityIdToIdMap[item.entityId] = item.id;
      });

      return {
        id,
        groupedIds: _.map(group, "id"),
        clientId,
        clientSecret,
        // credType,
        entityType,
        // messageType,
        serviceName,
        url,
        version,
        entityId: _.map(group, "entityId"),
        isDeleted,
        isToolEnabled,
        metadata,
        curriculumId: entityId,
        entityIdToIdMap,
        ..._.omit(rest, ["entityId"]),
      };
    })
    .value();

  return state;
};

export const getInitialStateV2 = ltiIntegrationsCreds => {
  if (_.isEmpty(ltiIntegrationsCreds)) {
    return [initialEmptyState];
  }

  const state = _.chain(ltiIntegrationsCreds)
    .groupBy(
      ltiCredsObject =>
        `${ltiCredsObject.clientId}-${ltiCredsObject.clientSecret}-${ltiCredsObject.url.launchUrl}`
    )
    .map(group => {
      const {
        clientId,
        clientSecret,
        credType,
        entityType,
        messageType,
        serviceName,
        url,
        version,
        isDeleted,
        id,
        isToolEnabled,
        metadata,
        entityId,
        ...rest
      } = _.head(group);

      // Create a mapping from entityId to id
      const entityIdToIdMap = {};
      group.forEach(item => {
        entityIdToIdMap[item.entityId] = item.id;
      });

      return {
        id,
        groupedIds: _.map(group, "id"),
        clientId,
        clientSecret,
        credType,
        entityType,
        messageType,
        serviceName,
        url,
        version,
        entityId: _.map(group, "entityId"),
        isDeleted,
        isToolEnabled,
        metadata,
        curriculumId: entityId,
        entityIdToIdMap,
        ..._.omit(rest, ["entityId"]),
      };
    })
    .value();

  return state;
};

export const getLTIServiceOptions = () => {
  return _.reduce(
    LTI_SERVICE_DETAILS,
    (result, value, key) => {
      if (ACLStore.can(value.featureFlag) || key === "CUSTOM") {
        result.push({
          value: key,
          label: value.label,
        });
      }
      return result;
    },
    []
  );
};

export const getLTIServiceOptionsV2 = () => {
  return _.reduce(
    LTI_SERVICE_DETAILS,
    (result, value, key) => {
      if (ACLStore.can(value.featureFlag)) {
        result.push({
          value: key,
          label: value.label,
        });
      }
      return result;
    },
    []
  );
};

export const getLtiServiceLabel = serviceName => {
  return _.get(LTI_NAME_MAP, _.toLower(serviceName), serviceName);
};

// Helper to get nested object values by path
export const getNestedValue = (obj, path) => {
  if (!path) return obj;
  const pathArray = path.split(".");
  return pathArray.reduce(
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
    obj
  );
};

/// Define common field configurations
const COMMON_FIELDS = {
  name: {
    type: "text",
    field: "name",
    label: "common:name",
    required: true,
    width: "100%",
    conditionalRender: fieldValue => fieldValue.serviceName === "CUSTOM",
  },
  clientId: {
    type: "text",
    field: "clientId",
    label: "integration:client_key",
    required: true,
    width: "100%",
  },
  clientSecret: {
    type: "password",
    field: "clientSecret",
    label: "integration:shared_secret",
    required: true,
    width: "100%",
  },
  launchUrl: {
    type: "textarea",
    field: "url.launchUrl",
    label: "integration:launch_url_plural",
    subLabel: "integration:lti_redirect_url_subtitle",
    placeholder: "integration:lti_redirect_url_placeholder",
    description: "integration:lti_launch_url_info",
    required: true,
    width: "100%",
    conditionalRender: fieldValue => {
      return _.isEmpty(
        _.get(
          LTI_SERVICE_DETAILS,
          [fieldValue.serviceName, "url", "launchUrl"],
          ""
        )
      );
    },
  },
  gradePassback: {
    type: "toggle",
    field: "metadata.gradePassback",
    label: "integration:allow_grade_passback",
    required: false,
    width: "100%",
    conditionalRender: fieldValue =>
      !["CUSTOM", "SPONTE"].includes(fieldValue.serviceName),
    description: "integration:allow_grade_passback_info",
  },
  gradePassbackHalf: {
    type: "toggle",
    field: "metadata.gradePassback",
    label: "integration:allow_grade_passback",
    required: false,
    width: "50%",
    conditionalRender: fieldValue =>
      !["CUSTOM", "SPONTE"].includes(fieldValue.serviceName),
    description: "integration:allow_grade_passback_info",
  },
  loginUrl: {
    type: "text",
    field: "url.loginUrl",
    label: "integration:initiate_login_url",
    required: true,
    width: "100%",
  },
  redirectUrl: {
    type: "textarea",
    field: "url.launchUrl",
    label: "integration:launch_or_redirect_url_plural",
    subLabel: "integration:lti_redirect_url_subtitle",
    placeholder: "integration:lti_redirect_url_placeholder",
    required: true,
    width: "100%",
    description: "integration:launch_redirect_url_info",
  },
  jwkUrl: {
    type: "text",
    field: "url.jwkUrl",
    label: "integration:keyset_url",
    required: true,
    width: "100%",
  },
  rosterAccess: {
    type: "toggle",
    field: "metadata.rosterAccess",
    label: "integration:allow_roster_access",
    required: false,
    width: "50%",
    conditionalRender: fieldValue =>
      !["CUSTOM", "SPONTE"].includes(fieldValue.serviceName),
    description: "integration:allow_roster_access_info",
  },
  iconUrl: {
    type: "text",
    field: "metadata.iconUrl",
    label: "integration:icon_url",
    required: false,
    width: "100%",
  },
  targetLinkUrl: {
    type: "text",
    field: "url.targetLinkUrl",
    label: "integration:target_link_uri",
    required: false,
    conditionalRender: fieldValue => fieldValue.serviceName === "TURNITIN",
    width: "100%",
  },
  customParameter: {
    type: "textarea",
    field: "metadata.custom_params",
    label: "integration:custom_parameter",
    subLabel: "integration:custom_parameter_formate",
    placeholder: "integration:custom_parameter_placeholder",
    required: false,
    conditionalRender: fieldValue => fieldValue.serviceName === "QUIZIZZ",
    width: "100%",
    description: "integration:custom_parameter_info",
  },
  placement: {
    type: "select",
    field: "metadata.placement",
    options: [
      // { label: "Curriculum", value: "HOME" },
      { label: "Assignment", value: "ASSIGNMENT" },
      { label: "Course", value: "COURSE" },
    ],
    isMulti: true,
    label: "common:placement",
    required: true,
    width: "100%",
  },
};

// Common advanced settings fields
const ADVANCED_FIELDS = {
  basicLaunch: {
    type: "select",
    field: "metadata.messageType",
    label: "integration:message_type",
    options: [
      { label: "Basic launch", value: "BASIC_LTI_LAUNCH" },
      {
        label: "Deep linking",
        value: "CONTENT_ITEM_SELECTION",
      },
    ],
    isMulti: false,
    isClearable: false,
    required: false,
    width: "50%",
  },
  privacyLevel: {
    type: "select",
    field: "metadata.privacyLevel",
    options: [
      { label: "Public", value: "PUBLIC" },
      { label: "Private", value: "PRIVATE" },
    ],
    isMulti: false,
    label: "integration:privacy_level",
    isClearable: false,
    required: false,
    width: "50%",
  },
  readingResults: {
    type: "toggle",
    field: "metadata.readingResults",
    label: "integration:allow_reading_result",
    required: false,
    width: "50%",
    description: "integration:allow_reading_result_info",
  },
  readingGradebook: {
    type: "toggle",
    field: "metadata.readingGradebook",
    label: "integration:allow_reading_gradebook",
    required: false,
    width: "50%",
    description: "integration:allow_reading_gradebook_info",
  },
  viewResponseAfterSubmission: {
    type: "toggle",
    field: "metadata.viewResponseAfterSubmission",
    label: "integration:view_response_after_submission",
    required: false,
    width: "50%",
    description: "integration:view_response_after_submission_info",
  },
};

const ADVANCED_FIELDS_1_0 = [
  { row: [ADVANCED_FIELDS.viewResponseAfterSubmission] },
];

const ADVANCED_FIELDS_1_1 = [
  {
    row: [ADVANCED_FIELDS.basicLaunch, ADVANCED_FIELDS.privacyLevel],
  },
  { row: [ADVANCED_FIELDS.viewResponseAfterSubmission] },
];

const ADVANCED_FIELDS_1_3 = [
  {
    row: [ADVANCED_FIELDS.basicLaunch, ADVANCED_FIELDS.privacyLevel],
  },
  {
    row: [ADVANCED_FIELDS.readingResults, ADVANCED_FIELDS.readingGradebook],
  },
  { row: [ADVANCED_FIELDS.viewResponseAfterSubmission] },
];

// Define default configuration using common fields
export const DEFAULT_FORM_CONFIG = {
  "1.0": [
    { row: [COMMON_FIELDS.name] },
    { row: [COMMON_FIELDS.clientId] },
    { row: [COMMON_FIELDS.clientSecret] },
    { row: [COMMON_FIELDS.launchUrl] },
  ],
  1.1: [
    { row: [COMMON_FIELDS.name] },
    { row: [COMMON_FIELDS.clientId] },
    { row: [COMMON_FIELDS.clientSecret] },
    { row: [COMMON_FIELDS.launchUrl] },
    { row: [COMMON_FIELDS.gradePassback] },
  ],
  1.3: [
    { row: [COMMON_FIELDS.name] },
    { row: [COMMON_FIELDS.loginUrl] },
    { row: [COMMON_FIELDS.redirectUrl] },
    { row: [COMMON_FIELDS.jwkUrl] },
    { row: [COMMON_FIELDS.customParameter] },
    { row: [COMMON_FIELDS.targetLinkUrl] },
    { row: [COMMON_FIELDS.gradePassbackHalf, COMMON_FIELDS.rosterAccess] },
  ],
};

// Service-specific configurations
export const SERVICE_FORM_CONFIG = {
  CUSTOM: {
    "1.0": [
      ...DEFAULT_FORM_CONFIG["1.0"],
      {
        row: [{ ...COMMON_FIELDS.targetLinkUrl, conditionalRender: undefined }],
      },
      { row: [COMMON_FIELDS.iconUrl] },
      { row: [COMMON_FIELDS.placement] },
      ...ADVANCED_FIELDS_1_0,
    ],
    1.1: [
      ...DEFAULT_FORM_CONFIG[1.1],
      {
        row: [{ ...COMMON_FIELDS.targetLinkUrl, conditionalRender: undefined }],
      },
      { row: [COMMON_FIELDS.iconUrl] },
      { row: [COMMON_FIELDS.placement] },
      {
        row: [{ ...COMMON_FIELDS.gradePassback, conditionalRender: undefined }],
      },
      ...ADVANCED_FIELDS_1_1,
    ],
    1.3: [
      ...DEFAULT_FORM_CONFIG[1.3],
      {
        row: [{ ...COMMON_FIELDS.targetLinkUrl, conditionalRender: undefined }],
      },
      { row: [COMMON_FIELDS.iconUrl] },
      {
        row: [
          { ...COMMON_FIELDS.customParameter, conditionalRender: undefined },
        ],
      },
      { row: [COMMON_FIELDS.placement] },
      {
        row: [
          { ...COMMON_FIELDS.gradePassbackHalf, conditionalRender: undefined },
          { ...COMMON_FIELDS.rosterAccess, conditionalRender: undefined },
        ],
      },
      ...ADVANCED_FIELDS_1_3,
    ],
  },
  MS365: {
    1.3: [...DEFAULT_FORM_CONFIG[1.3], { row: [COMMON_FIELDS.placement] }],
  },
  QUIZIZZ: {
    1.3: [...DEFAULT_FORM_CONFIG[1.3], { row: [COMMON_FIELDS.placement] }],
  },

  // Default configuration for any service not specifically defined
  default: {
    "1.0": [
      ...DEFAULT_FORM_CONFIG["1.0"],
      { row: [ADVANCED_FIELDS.viewResponseAfterSubmission] },
    ],
    1.1: [
      ...DEFAULT_FORM_CONFIG[1.1],
      { row: [ADVANCED_FIELDS.viewResponseAfterSubmission] },
    ],
    1.3: [
      ...DEFAULT_FORM_CONFIG[1.3],
      { row: [ADVANCED_FIELDS.viewResponseAfterSubmission] },
    ],
  },
};

export const ltiToolImages = {
  clickview: `${LTI_ICON_BASE_URL}/clickview.png`,
  edpuzzle: `${LTI_ICON_BASE_URL}/edpuzzle.jpeg`,
  educaplay: `${LTI_ICON_BASE_URL}/eduplay.png`,
  h5p: `${LTI_ICON_BASE_URL}/h5p.jpeg`,
  mcgrawhill: `${LTI_ICON_BASE_URL}/mcGrawlhill.jpeg`,
  onenote: `${LTI_ICON_BASE_URL}/one_note.jpeg`,
  wayground: `${LTI_ICON_BASE_URL}/wayground.png`,
  "mais serviços": `${LTI_ICON_BASE_URL}/sponte.jpeg`,
  "turnitin feedback studio": `${LTI_ICON_BASE_URL}/turnitin.jpeg`,
  kognity: `${LTI_ICON_BASE_URL}/kognity.jpeg`,
  "microsoft education": `${LTI_ICON_BASE_URL}/ms365.png`,
  atomi: `${LTI_ICON_BASE_URL}/atomi-logomark.png`,
};

// Helper to get the field configuration based on the service name and version

/**
 * Returns the field configuration for the given field value.
 * @param {Object} fieldValue
 * @returns {Array}
 */
export const getFieldConfig = fieldValue => {
  const serviceConfig =
    SERVICE_FORM_CONFIG[fieldValue.serviceName] || SERVICE_FORM_CONFIG.default;

  return fieldValue.version ? serviceConfig[fieldValue.version] || [] : [];
};

// Function to check if "Generate Credentials" button should be disabled
export const isGenerateCredsDisabled = fieldValue => {
  const hasCurriculum =
    Array.isArray(fieldValue.entityId) && fieldValue.entityId.length > 0;
  const hasVersion = Boolean(fieldValue.version);
  const hasServiceName = Boolean(fieldValue.serviceName);

  const isCustom = fieldValue.serviceName === "CUSTOM";
  const hasCustomName = isCustom ? Boolean(fieldValue.name?.trim()) : true;
  const isValidName = isCustom ? !fieldValue.serviceNameError : true;
  const isValidCustomName =
    isCustom && fieldValue.name ? !fieldValue.name.includes("_") : true;

  return !(
    hasCurriculum &&
    hasVersion &&
    hasServiceName &&
    hasCustomName &&
    isValidName &&
    isValidCustomName
  );
};

//  Function to check if "Create lti" button should be disabled
export const isFormValid = fieldValue => {
  const isKognityService = _.isEqual(_.get(fieldValue, "serviceName"), KOGNITY);
  if (isKognityService) {
    return true;
  }

  const isCustom = fieldValue.serviceName === "CUSTOM";

  const isValidName =
    isCustom &&
    (Boolean(fieldValue.serviceNameError) || fieldValue.name.includes("_"));

  if (
    !fieldValue.serviceName ||
    !fieldValue.version ||
    _.isEmpty(fieldValue.entityId) ||
    _.isEmpty(fieldValue.metadata.placement) ||
    isValidName ||
    Boolean(fieldValue.validUrlError)
  ) {
    return false;
  }

  // Get required fields from config
  const fieldConfig = getFieldConfig(fieldValue);
  const requiredFields = fieldConfig
    .flatMap(rowConfig => rowConfig.row)
    .filter(
      field =>
        field.required &&
        (!field.conditionalRender || field.conditionalRender(fieldValue))
    );

  // Check if all required fields have values
  return requiredFields.every(field => {
    const value = getNestedValue(fieldValue, field.field);
    return value !== undefined && value !== null && value !== "";
  });
};

/**
 * Utility to generate a key-value object mapping translated field labels
 * to their corresponding values from a given data object.
 *
 * It is typically used to render credential info in a user-friendly format.
 *
 * @constant {Array} LTI_PLATFORM_CREDS_FIELDS - List of fields with `path` and `localeKey`
 * @param {Object} data - Object from which values are to be picked using the path
 * @param {Function} t - Translation function (usually from `react-i18next`)
 * @returns {Object} - Object with translated labels as keys and corresponding values from data
 */
export const generateLtiCredsDisplayMap = (fieldValue, t) => {
  return _.reduce(
    LTI_PLATFORM_CREDS_FIELDS,
    (result, { field, path, localeKey }) => {
      const label = t(`integration:${localeKey}`);
      const value =
        field === "deploymentId"
          ? _.get(fieldValue, "deploymentId", "-")
          : _.get(fieldValue, path, "-");
      result[label] = value;
      return result;
    },
    {}
  );
};

export const generateKognityCredsDisplayMap = (fieldValue, t) => {
  return _.reduce(
    KOGNITY_CREDS_FIELDS,
    (result, { path, localeKey }) => {
      const label = t(`integration:${localeKey}`);
      result[label] = _.get(fieldValue, path, "-");
      return result;
    },
    {}
  );
};

// Function to get all native LTI app which toddle supports
export const getLtiServiceNames = () => {
  return Object.keys(LTI_SERVICE_DETAILS)
    .filter(key => key !== "CUSTOM")
    .map(key => ({
      serviceName: key,
    }));
};

// Helper function to format custom parameters
export const formatCustomParams = paramsObject => {
  // Check if paramsObject is null, undefined, or not an object
  if (
    !paramsObject ||
    typeof paramsObject !== "object" ||
    Array.isArray(paramsObject)
  ) {
    return ""; // Return empty string for invalid inputs
  }

  return Object.entries(paramsObject)
    .map(([key, value]) => `${key}=${value}`)
    .join("\n");
};

/**
 * Normalizes a launch URL input into a clean string.
 *
 * - If the input is an array, it will:
 *    1. Convert each element to a string.
 *    2. Trim extra spaces from each element.
 *    3. Remove empty, null, or undefined values.
 *    4. Join all elements with a newline ("\n") separator.
 *
 * - If the input is a string (or other type), it will:
 *    1. Convert it to a string safely.
 *    2. Trim leading and trailing spaces.
 *
 * This ensures the launch URL(s) are consistently formatted —
 * if multiple URLs are provided, each appears on its own line.
 *
 * Examples:
 *   normalizeLaunchUrl([" a.com ", null, "b.com"])
 *   // "a.com\nb.com"
 *
 *   normalizeLaunchUrl("   https://a.com   ")
 *   // "https://a.com"
 *
 *   normalizeLaunchUrl(null)
 *   // ""
 */
export const normalizeLaunchUrl = input => {
  if (_.isArray(input)) {
    return _(input)
      .map(u => _.trim(_.toString(u))) // convert + trim
      .filter() // remove falsy
      .join("\n"); // join each URL on new line
  }

  return _.trim(_.toString(input || ""));
};

/**
 * Parses a launch URL input into either a clean string or an array of strings.
 *
 * - If the input (or raw value) contains multiple lines (split by "\n"),
 *   returns an array of trimmed, non-empty URLs.
 * - If only a single URL is present, returns that as a string.
 *
 * Works safely even if input is already an array or contains extra whitespace.
 *
 * Examples:
 *   parseLaunchUrl("https://a.com\nhttps://b.com")
 *   // ["https://a.com", "https://b.com"]
 *
 *   parseLaunchUrl("https://a.com")
 *   // "https://a.com"
 */
export const parseLaunchUrl = group => {
  const rawLaunchUrl = _.get(group, "url.launchUrl", "");

  if (_.isArray(rawLaunchUrl)) {
    // Trim each and filter out empties
    return _(rawLaunchUrl)
      .map(u => _.trim(u))
      .filter(Boolean)
      .value();
  }

  // Normalize any type into a clean string
  const normalized = _.trim(_.toString(rawLaunchUrl));

  // If multiple lines present, convert to array
  if (normalized.includes("\n")) {
    return _(normalized.split("\n"))
      .map(u => _.trim(u))
      .filter(Boolean)
      .value();
  }

  // Single URL case
  return normalized;
};

/**
 * Checks if a given string contains one or more valid URLs (one per line).
 *
 * - Each line is trimmed and validated individually.
 * - Empty lines are ignored.
 * - A URL is considered valid if it starts with http:// or https:// and follows
 *   a basic domain/path structure.
 *
 * Examples:
 *   "https://a.com\nhttps://b.org"  → ✅ true
 *   "https://a.com\ninvalid"        → ❌ false
 */
export const isValidHttpUrl = url => {
  try {
    const parsed = new URL(url);
    const protocolValid =
      parsed.protocol === "http:" || parsed.protocol === "https:";
    const hostnameValid = !!parsed.hostname;
    return protocolValid && hostnameValid;
  } catch {
    return false;
  }
};

export const validateMultiLineUrls = input => {
  if (!_.isString(input)) {
    return false;
  }
  const urls = _.chain(input)
    .split(/\r?\n/) // split by new lines
    .map(_.trim) // trim spaces
    .filter(Boolean) // remove empty lines
    .value();

  // Ensure every URL passes validation
  return urls.length > 0 && urls.every(isValidHttpUrl);
};
